import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, CreditCard, Smartphone, CheckCircle, XCircle } from 'react-feather';
import { formatCurrencyWithConversion } from '../utils/currency';
import { depositsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import './DepositCheckout.css';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 75; // ~5 minutes

export default function DepositCheckout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const checkout = location.state;

  const isFapshiDirect = checkout?.isDirectPay === true && checkout?.gatewayId === 'fapshi';

  // Fapshi Direct Pay state
  const [phone, setPhone] = useState('');
  const [depositing, setDepositing] = useState(false);
  const [depositError, setDepositError] = useState('');
  const [pendingId, setPendingId] = useState(() => {
    return sessionStorage.getItem('nb_fapshi_pending') || null;
  });
  const [pollStatus, setPollStatus] = useState(null); // 'pending' | 'completed' | 'failed'
  const [networkError, setNetworkError] = useState(false);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);

  // Persist pendingId to survive accidental page refreshes
  useEffect(() => {
    if (pendingId) {
      sessionStorage.setItem('nb_fapshi_pending', pendingId);
    } else {
      sessionStorage.removeItem('nb_fapshi_pending');
    }
  }, [pendingId]);

  // Redirect if no checkout state and not fapshi direct-pay
  useEffect(() => {
    if (!isFapshiDirect && !checkout?.paymentUrl) {
      navigate('/dashboard/deposit', { replace: true });
    }
  }, [checkout, navigate, isFapshiDirect]);

  // Auto-navigate back after successful payment
  useEffect(() => {
    if (pollStatus === 'completed') {
      const timer = setTimeout(() => {
        navigate('/dashboard/deposit');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [pollStatus, navigate]);

  // Polling for Fapshi Direct Pay
  useEffect(() => {
    if (!pendingId) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    pollCountRef.current = 0;

    const poll = async () => {
      try {
        const res = await depositsAPI.fapshiDirectPayStatus(pendingId);
        const status = (res.data?.status || '').toUpperCase();
        setNetworkError(false);

        if (status === 'SUCCESSFUL' || status === 'COMPLETED') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPollStatus('completed');
          setPendingId(null);
          refreshUser({ force: true });
        } else if (status === 'FAILED' || status === 'FAILLED') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPollStatus('failed');
          setPendingId(null);
          setDepositError('Mobile Money payment was declined or failed. Please try again.');
        }
      } catch (err) {
        setNetworkError(true);
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [pendingId, refreshUser]);

  const handleFapshiDeposit = async () => {
    const normalised = phone.trim().replace(/\D/g, '');
    if (!normalised) {
      setDepositError('Please enter your Mobile Money phone number.');
      return;
    }
    setDepositError('');
    setDepositing(true);
    try {
      const res = await depositsAPI.fapshiDirectPay(checkout.xafAmount, normalised);
      setPendingId(String(res.data.deposit_id));
      setPollStatus('pending');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      setDepositError(msg || 'Failed to send push. Check your number and try again.');
    } finally {
      setDepositing(false);
    }
  };

  const openProviderCheckout = () => {
    window.location.href = checkout.paymentUrl;
  };

  if (!isFapshiDirect && !checkout?.paymentUrl) return null;

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="deposit-checkout-top-actions">
        <button
          className="btn btn-secondary deposit-checkout-back-btn"
          type="button"
          onClick={() => navigate('/dashboard/deposit')}
        >
          <ArrowLeft size={16} /> Back to Deposit
        </button>
      </div>

      <h1 className="page-title">Checkout</h1>
      <p className="page-subtitle">
        {isFapshiDirect
          ? 'Enter your Mobile Money number to receive a payment push on your phone.'
          : 'Review your deposit, then continue to the payment provider.'}
      </p>

      <div className="card" style={{ marginBottom: 20 }}>
        {/* Header */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 14,
            background: isFapshiDirect ? 'rgba(249,115,22,0.12)' : 'var(--accent-glow)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: isFapshiDirect ? '#f97316' : 'var(--accent)',
          }}>
            {isFapshiDirect ? <Smartphone size={20} /> : <CreditCard size={20} />}
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, fontFamily: 'Syne, sans-serif' }}>
              {checkout.gateway === 'Helaket' ? 'Crypto' : checkout.gateway} Checkout
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>
              {isFapshiDirect
                ? 'MTN Mobile Money or Orange Money — Cameroon'
                : 'You can return to deposits before opening the payment provider.'}
            </div>
          </div>
        </div>

        {/* Amount summary */}
        <div style={{
          padding: '16px 18px', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', background: 'var(--bg-elevated)', marginBottom: 18,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
            <span style={{ color: 'var(--text-muted)' }}>Deposit amount</span>
            <strong>{checkout.displayAmount}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
            <span style={{ color: 'var(--text-muted)' }}>Estimated credit</span>
            <strong style={{ color: 'var(--green-500)' }}>
              {formatCurrencyWithConversion(checkout.creditedUsd || 0, 'USD', 2)}
            </strong>
          </div>
          {isFapshiDirect && checkout.xafAmount && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 8 }}>
              <span style={{ color: 'var(--text-muted)' }}>Charge in FCFA</span>
              <strong>{Number(checkout.xafAmount).toLocaleString()} XAF</strong>
            </div>
          )}
        </div>

        {/* ── Fapshi Direct Pay UI ── */}
        {isFapshiDirect && (
          <>
            {pollStatus === 'completed' ? (
              <div style={{ textAlign: 'center', padding: '28px 0' }}>
                <CheckCircle size={52} style={{ color: 'var(--green-500)', marginBottom: 16 }} />
                <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>Payment Approved!</div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24 }}>
                  Your Mobile Money payment was confirmed. Your balance has been credited.
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/dashboard/deposit')}
                >
                  Back to Deposits
                </button>
              </div>
            ) : pollStatus === 'failed' ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <XCircle size={48} style={{ color: 'var(--red-500, #ef4444)', marginBottom: 14 }} />
                <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Payment Failed</div>
                {depositError && (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
                    {depositError}
                  </div>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={() => { setPollStatus(null); setDepositError(''); setPhone(''); }}
                >
                  Try Again
                </button>
              </div>
            ) : pendingId ? (
              /* Polling / waiting state */
              <div style={{ textAlign: 'center', padding: '28px 0' }}>
                <div style={{
                  width: 56, height: 56, margin: '0 auto 20px',
                  border: '3px solid rgba(249,115,22,0.2)', borderTopColor: '#f97316',
                  borderRadius: '50%', animation: 'spin 0.9s linear infinite',
                }} />
                <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 8 }}>
                  Waiting for your approval
                </div>
                {networkError && (
                  <div style={{ fontSize: 13, color: 'var(--red-500, #ef4444)', marginTop: 12, fontWeight: 600 }}>
                    Connection temporarily unavailable. Retrying...
                  </div>
                )}
              </div>
            ) : (
              /* Phone input + Deposit button */
              <>
                {depositError && (
                  <div className="alert alert-error" style={{ marginBottom: 16 }}>{depositError}</div>
                )}
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Mobile Money Number</label>
                  <input
                    className="form-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g. 6XXXXXXXX"
                    autoComplete="tel"
                    disabled={depositing}
                    onKeyDown={(e) => e.key === 'Enter' && handleFapshiDeposit()}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>
                    MTN Mobile Money or Orange Money number — Cameroon only
                  </div>
                </div>
                <button
                  className="btn btn-lg deposit-checkout-pay-btn"
                  type="button"
                  onClick={handleFapshiDeposit}
                  disabled={depositing || !phone.trim()}
                  style={{ background: '#f97316', border: 'none', width: '100%', boxShadow: depositing ? 'none' : '0 4px 20px rgba(249,115,22,0.35)' }}
                >
                  {depositing ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2, borderTopColor: '#fff' }} />
                      Sending push...
                    </span>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <Smartphone size={16} />
                      Deposit
                    </span>
                  )}
                </button>
              </>
            )}
          </>
        )}

        {/* ── Standard redirect UI (all other gateways) ── */}
        {!isFapshiDirect && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button className="btn btn-primary deposit-checkout-pay-btn" type="button" onClick={openProviderCheckout}>
              <ExternalLink size={16} /> Continue to Payment
            </button>
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className="card" style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.7 }}>
        {isFapshiDirect ? (
          <>
            <div>Enter your MTN or Orange Money number and click <strong>Deposit</strong>.</div>
            <div>You will receive a push notification — approve it directly from your mobile money app.</div>
            <div>Your balance updates within seconds of approval.</div>
          </>
        ) : (
          <>
            <div>After your payment finishes, the provider will send you back to the deposit page automatically.</div>
            <div>If the payment is still pending when you return, your deposit history will update as soon as the gateway confirms it.</div>
          </>
        )}
      </div>
    </div>
  );
}
